import React, { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { BrowserCompatWrapper } from '@/components/ui/BrowserCompatWrapper';
import { DeletePreRegistrationDialog } from './DeletePreRegistrationDialog';
import { useChangePreRegistrationStatus } from '../hooks/usePreRegisteredAccommodationMutations';
import {
  MANUAL_STATUS_ACTIONS,
  PRE_REGISTRATION_STATUS_IDS,
  type StatusAction,
} from '../utils/status-transitions.utils';
import type { MunicipalListAccommodation } from '../types/pre-registration.types';

interface PreRegistrationStatusMenuProps {
  row: MunicipalListAccommodation;
  // Extra status entries (e.g. linking an official REPAT accommodation, KER-568), listed first
  children?: React.ReactNode;
}

// Status actions of one row; only the transitions allowed from its current status
export const PreRegistrationStatusMenu: React.FC<PreRegistrationStatusMenuProps> = ({
  row,
  children,
}) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const changeStatus = useChangePreRegistrationStatus();
  const actions = MANUAL_STATUS_ACTIONS[row.status.code];

  const runAction = (action: StatusAction) => {
    if (action.asksReason) {
      setDeleteOpen(true);
      return;
    }
    changeStatus.mutate({
      id: row.id,
      payload: { statusId: PRE_REGISTRATION_STATUS_IDS[action.target] },
    });
  };

  const confirmDelete = (reason: string) => {
    changeStatus.mutate(
      {
        id: row.id,
        payload: { statusId: PRE_REGISTRATION_STATUS_IDS.deleted, observations: reason },
      },
      { onSuccess: () => setDeleteOpen(false) }
    );
  };

  return (
    <BrowserCompatWrapper>
      {/* Non-modal so the delete dialog can take focus after the menu closes */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            title="Cambiar estado"
            disabled={changeStatus.isPending}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Cambiar estado</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {children}
          {actions.map((action) => (
            <DropdownMenuItem
              key={action.target}
              onSelect={() => runAction(action)}
              className={action.target === 'deleted' ? 'text-destructive' : undefined}
            >
              {action.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DeletePreRegistrationDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        establishmentName={row.establishmentName}
        currentObservations={row.observations}
        isPending={changeStatus.isPending}
        onConfirm={confirmDelete}
      />
    </BrowserCompatWrapper>
  );
};
